import { useEffect, useRef, useState, useCallback } from 'react';
import { Navigation, Clock, AlertCircle, Loader2, Wifi, WifiOff } from 'lucide-react';
import { devLog, devError } from '../lib/devLog';
import { setSafeHTML } from '../lib/safeHtml';

const MAPBOX_VERSION = '3.0.1';

// Load Mapbox from CDN (avoids worker/webpack issues in production)
const loadMapboxCDN = () => {
  return new Promise((resolve, reject) => {
    if (window.mapboxgl) { resolve(window.mapboxgl); return; }
    const link = document.createElement('link');
    link.href = `https://api.mapbox.com/mapbox-gl-js/v${MAPBOX_VERSION}/mapbox-gl.css`;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    const script = document.createElement('script');
    script.src = `https://api.mapbox.com/mapbox-gl-js/v${MAPBOX_VERSION}/mapbox-gl.js`;
    script.async = true;
    script.onload = () => resolve(window.mapboxgl);
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

const DriverTrackingMap = ({ 
  job, 
  driverLocation: initialDriverLocation,
  height = '250px',
  onLocationUpdate 
}) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const driverMarker = useRef(null);
  const pickupMarker = useRef(null);
  const dropoffMarker = useRef(null);
  const routeLayerAdded = useRef(false);
  const animationFrame = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState(null);
  const [driverLocation, setDriverLocation] = useState(initialDriverLocation);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [estimatedArrival, setEstimatedArrival] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [pickupCoords, setPickupCoords] = useState(null);
  const [dropoffCoords, setDropoffCoords] = useState(null);

  // Geocode addresses to coordinates
  const geocodeAddress = useCallback(async (address) => {
    const token = process.env.REACT_APP_MAPBOX_TOKEN;
    if (!token) return null;
    
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${token}&limit=1`
      );
      const data = await response.json();
      if (data.features && data.features.length > 0) {
        return data.features[0].center;
      }
    } catch (error) {
      devError('Geocoding error:', error);
    }
    return null;
  }, []);

  // Get route from Mapbox Directions API
  const getRoute = useCallback(async (startCoords, endCoords) => {
    const token = process.env.REACT_APP_MAPBOX_TOKEN;
    if (!token || !startCoords || !endCoords) return null;
    
    try {
      const response = await fetch(
        `https://api.mapbox.com/directions/v5/mapbox/driving/${startCoords[0]},${startCoords[1]};${endCoords[0]},${endCoords[1]}?geometries=geojson&access_token=${token}`
      );
      const data = await response.json();
      if (data.routes && data.routes.length > 0) {
        return {
          geometry: data.routes[0].geometry,
          duration: data.routes[0].duration,
          distance: data.routes[0].distance
        };
      }
    } catch (error) {
      devError('Directions error:', error);
    }
    return null;
  }, []);

  // Draw route line on map
  const drawRoute = useCallback(async (startCoords, endCoords, routeId = 'driver-route') => {
    if (!map.current || !mapLoaded) return;
    
    const routeData = await getRoute(startCoords, endCoords);
    if (!routeData) return;
    
    // Update ETA based on route duration
    const minutes = Math.ceil(routeData.duration / 60);
    if (minutes < 1) setEstimatedArrival('Arriving now');
    else if (minutes === 1) setEstimatedArrival('1 minute away');
    else if (minutes < 60) setEstimatedArrival(`${minutes} minutes away`);
    else {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      setEstimatedArrival(`${hours}h ${mins}m away`);
    }
    
    // Remove existing route if any
    if (map.current.getSource(routeId)) {
      map.current.removeLayer(`${routeId}-line`);
      map.current.removeLayer(`${routeId}-outline`);
      map.current.removeSource(routeId);
    }
    
    // Add route source
    map.current.addSource(routeId, {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {},
        geometry: routeData.geometry
      }
    });
    
    // Add route outline
    map.current.addLayer({
      id: `${routeId}-outline`,
      type: 'line',
      source: routeId,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: { 'line-color': '#1e40af', 'line-width': 8, 'line-opacity': 0.3 }
    });
    
    // Add route line with animated dash
    map.current.addLayer({
      id: `${routeId}-line`,
      type: 'line',
      source: routeId,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: { 'line-color': '#3b82f6', 'line-width': 4, 'line-opacity': 0.9 }
    });
    
    routeLayerAdded.current = true;
  }, [mapLoaded, getRoute]);

  // Connect to WebSocket for real-time updates
  const connectWebSocket = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token || !job?.id) return;
    
    const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const wsUrl = backendUrl.replace(/^http/, 'ws') + `/api/ws/track/${job.id}?token=${token}`;
    
    try {
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        devLog('WebSocket connected for job tracking');
        setWsConnected(true);
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          if (message.type === 'location_update') {
            const locData = message.data;
            setDriverLocation(locData);
            setLastUpdate(new Date(locData.updated_at));
            
            if (locData.latitude && locData.longitude) {
              const driverCoords = [locData.longitude, locData.latitude];
              const destCoords = job.status === 'accepted' ? pickupCoords : dropoffCoords;
              if (destCoords) {
                drawRoute(driverCoords, destCoords);
              }
            }
            
            if (onLocationUpdate) onLocationUpdate(locData);
          } else if (message.type === 'connected' && message.driver_location) {
            setDriverLocation(message.driver_location);
          }
        } catch (error) {
          devError('WebSocket message error:', error);
        }
      };
      
      wsRef.current.onclose = () => {
        setWsConnected(false);
        reconnectTimeoutRef.current = setTimeout(() => {
          if (job?.status && ['accepted', 'in_progress'].includes(job.status)) {
            connectWebSocket();
          }
        }, 3000);
      };
      
      wsRef.current.onerror = () => setWsConnected(false);
      
      const pingInterval = setInterval(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send('ping');
        }
      }, 30000);
      
      return () => clearInterval(pingInterval);
    } catch (error) {
      devError('WebSocket connection error:', error);
    }
  }, [job?.id, job?.status, pickupCoords, dropoffCoords, drawRoute, onLocationUpdate]);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    const token = process.env.REACT_APP_MAPBOX_TOKEN;
    if (!token || token === 'YOUR_MAPBOX_TOKEN_HERE') {
      setMapError('Map not available');
      return;
    }

    const initMap = async () => {
      try {
        const mapboxgl = await loadMapboxCDN();
        mapboxgl.accessToken = token;

        map.current = new mapboxgl.Map({
          container: mapContainer.current,
          style: 'mapbox://styles/mapbox/streets-v12',
          center: [-89.7932, 38.4931],
          zoom: 12,
          attributionControl: false,
        });

        map.current.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');

        map.current.on('load', async () => {
          setMapLoaded(true);

          if (job.pickup_address) {
            const coords = await geocodeAddress(job.pickup_address);
            if (coords) {
              setPickupCoords(coords);
              const el = document.createElement('div');
              setSafeHTML(el, `<div style="width:36px;height:36px;background:linear-gradient(135deg,#22C55E,#16A34A);border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5"><circle cx="12" cy="12" r="3"></circle><path d="M12 2v4m0 12v4m10-10h-4M6 12H2"></path></svg></div>`);
              pickupMarker.current = new mapboxgl.Marker({ element: el })
                .setLngLat(coords)
                .setPopup(new mapboxgl.Popup({ offset: 25, closeButton: false }).setText('Pickup: ' + (job.pickup_address || '')))
                .addTo(map.current);
            }
          }

          if (job.dropoff_address) {
            const coords = await geocodeAddress(job.dropoff_address);
            if (coords) {
              setDropoffCoords(coords);
              const el = document.createElement('div');
              setSafeHTML(el, `<div style="width:36px;height:36px;background:linear-gradient(135deg,#EF4444,#DC2626);border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg></div>`);
              dropoffMarker.current = new mapboxgl.Marker({ element: el })
                .setLngLat(coords)
                .setPopup(new mapboxgl.Popup({ offset: 25, closeButton: false }).setText('Dropoff: ' + (job.dropoff_address || '')))
                .addTo(map.current);

              const bounds = new mapboxgl.LngLatBounds();
              if (pickupMarker.current) bounds.extend(pickupMarker.current.getLngLat());
              bounds.extend(coords);
              map.current.fitBounds(bounds, { padding: { top: 60, bottom: 60, left: 60, right: 60 }, maxZoom: 14 });
            }
          }
        });

        map.current.on('error', () => setMapError('Failed to load map'));
      } catch (error) {
        devError('Map initialization error:', error);
        setMapError('Failed to initialize map');
      }
    };

    initMap();

    return () => {
      if (animationFrame.current) cancelAnimationFrame(animationFrame.current);
      if (map.current) { map.current.remove(); map.current = null; }
    };
  }, [job.pickup_address, job.dropoff_address, geocodeAddress]);

  // Connect WebSocket when map is loaded
  useEffect(() => {
    if (mapLoaded && job?.id && ['accepted', 'in_progress'].includes(job.status)) {
      connectWebSocket();
    }
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    };
  }, [mapLoaded, job?.id, job?.status, connectWebSocket]);

  // Update driver marker with smooth animation
  useEffect(() => {
    if (!map.current || !mapLoaded || !driverLocation) return;

    const { latitude, longitude } = driverLocation;
    if (!latitude || !longitude) return;

    setLastUpdate(new Date(driverLocation.updated_at));

    const driverCoords = [longitude, latitude];
    const destCoords = job.status === 'accepted' ? pickupCoords : dropoffCoords;
    if (destCoords && !wsConnected) {
      drawRoute(driverCoords, destCoords);
    }

    if (!driverMarker.current) {
      const el = document.createElement('div');
      setSafeHTML(el, `<div style="position:relative;">
        <div style="position:absolute;width:60px;height:60px;left:-10px;top:-10px;background:rgba(249,115,22,0.25);border-radius:50%;animation:driverPulse 2s ease-in-out infinite;"></div>
        <div style="width:44px;height:44px;background:linear-gradient(135deg,#F97316,#EA580C);border-radius:50%;border:4px solid white;box-shadow:0 4px 14px rgba(249,115,22,0.5);display:flex;align-items:center;justify-content:center;position:relative;z-index:1;">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"><path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9L18 10l-2-4H8L6 10l-2.5 1.1C2.7 11.3 2 12.1 2 13v3c0 .6.4 1 1 1h2"></path><circle cx="7" cy="17" r="2"></circle><circle cx="17" cy="17" r="2"></circle></svg>
        </div></div>`);
      
      if (!document.getElementById('driver-pulse-style')) {
        const style = document.createElement('style');
        style.id = 'driver-pulse-style';
        style.textContent = `@keyframes driverPulse { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.4); opacity: 0.4; } }`;
        document.head.appendChild(style);
      }

      driverMarker.current = new mapboxgl.Marker({ element: el, anchor: 'center' })
        .setLngLat([longitude, latitude])
        .addTo(map.current);
    } else {
      const currentLngLat = driverMarker.current.getLngLat();
      const startLng = currentLngLat.lng, startLat = currentLngLat.lat;
      const endLng = longitude, endLat = latitude;
      const duration = 1000;
      const startTime = Date.now();

      const animate = () => {
        const progress = Math.min((Date.now() - startTime) / duration, 1);
        const ease = 1 - Math.pow(1 - progress, 3);
        driverMarker.current.setLngLat([startLng + (endLng - startLng) * ease, startLat + (endLat - startLat) * ease]);
        if (progress < 1) animationFrame.current = requestAnimationFrame(animate);
      };
      
      if (animationFrame.current) cancelAnimationFrame(animationFrame.current);
      animate();
    }

    const bounds = new mapboxgl.LngLatBounds();
    bounds.extend([longitude, latitude]);
    if (pickupMarker.current) bounds.extend(pickupMarker.current.getLngLat());
    if (dropoffMarker.current) bounds.extend(dropoffMarker.current.getLngLat());
    map.current.fitBounds(bounds, { padding: { top: 50, bottom: 50, left: 50, right: 50 }, maxZoom: 14, duration: 500 });
  }, [driverLocation, mapLoaded, job.status, pickupCoords, dropoffCoords, drawRoute, wsConnected]);

  if (mapError) {
    return (
      <div className="bg-zinc-100 rounded-xl flex items-center justify-center" style={{ height }}>
        <div className="text-center">
          <AlertCircle className="w-8 h-8 text-zinc-400 mx-auto mb-2" />
          <p className="text-sm text-zinc-500">{mapError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="relative rounded-xl overflow-hidden border border-zinc-200" style={{ height }}>
        <div ref={mapContainer} className="w-full h-full" />
        
        {!mapLoaded && (
          <div className="absolute inset-0 bg-zinc-100 flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
          </div>
        )}

        {driverLocation && mapLoaded && (
          <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg border border-zinc-100">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-orange-100 rounded-full flex items-center justify-center">
                <Navigation className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-zinc-900">{job.driver_name || 'Driver'}</p>
                <p className="text-xs text-green-600 font-medium">{estimatedArrival || 'Calculating ETA...'}</p>
              </div>
            </div>
          </div>
        )}

        <div className={`absolute top-3 right-12 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${wsConnected ? 'bg-green-100 text-green-700' : 'bg-zinc-100 text-zinc-500'}`}>
          {wsConnected ? <><Wifi className="w-3 h-3" /> Live</> : <><WifiOff className="w-3 h-3" /> Polling</>}
        </div>

        <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg border border-zinc-100">
          <div className="flex items-center gap-3 text-xs">
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-green-500"></div><span className="text-zinc-600">Pickup</span></div>
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-red-500"></div><span className="text-zinc-600">Dropoff</span></div>
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-orange-500"></div><span className="text-zinc-600">Driver</span></div>
            <div className="flex items-center gap-1.5"><div className="w-6 h-0.5 bg-blue-500 rounded"></div><span className="text-zinc-600">Route</span></div>
          </div>
        </div>
      </div>

      {lastUpdate && (
        <div className="flex items-center justify-center gap-2 text-xs text-zinc-400">
          <Clock className="w-3 h-3" />
          <span>Updated: {lastUpdate.toLocaleTimeString()}</span>
        </div>
      )}
    </div>
  );
};

export default DriverTrackingMap;
